// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useMemo,
	useState,
} from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// types
import {
	Category,
	Component,
	Contract,
	ContractAmount,
	ContractPayload,
	Entity,
	ErrorCause,
	ExpenseType,
	Feedback,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, SelectChangeEvent } from '@mui/material';

// fields
import { AmountsMultiCurrency } from '../../fields/AmountsMultiCurrency/AmountsMultiCurrency';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// utils
import { formatDate } from '../../../utils/dates';
import { getIds } from '../../../utils/objects';
import { translateError } from '../../../utils/translations';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface FormManageContractProps {
	contract?: Contract;
	entity?: Entity;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormManageContract = ({
	contract: contractProp,
	entity,
	onClose,
	onSuccess,
	setIsLoading,
}: FormManageContractProps) => {
	// hooks
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// params
	const paramDialogContract = Number(searchParams.get('dialogContract')) || undefined;

	const valuesDefault = useMemo(() => {
		let defaultAmounts: ContractPayload['amounts'] = [];
		let defaultCategories: number[] = [];
		let defaultComponents: number[] = [];
		let defaultEntities: number[] = [];
		let defaultExpenseTypes: number[] = [];

		if (contractProp?.categories) {
			defaultCategories = getIds(contractProp.categories as Category[] | number[]);
		}

		if (contractProp?.components) {
			defaultComponents = getIds(contractProp.components as Component[] | number[]);
		}

		if (contractProp?.entities) {
			defaultEntities = (contractProp.entities as Entity[]).map((entityItem) => entityItem.id);
		}

		if (contractProp?.expenseTypes) {
			defaultExpenseTypes = getIds(contractProp.expenseTypes as ExpenseType[] | number[]);
		}

		if (contractProp?.amounts) {
			defaultAmounts = contractProp.amounts;
		} else {
			// Default to one empty amount for new contracts
			defaultAmounts = [
				{
					amount: 0,
					currency: '',
				},
			];
		}

		if (entity) {
			defaultEntities = [entity.id];
		}

		return {
			amounts: defaultAmounts,
			categories: defaultCategories,
			comment: '',
			components: defaultComponents,
			dateSigning: formatDate({ date: contractProp?.dateSigning, format: 'YYYY-MM-DD' }),
			description: contractProp?.description || '',
			entities: defaultEntities,
			expenseTypes: defaultExpenseTypes,
			idStep: contractProp?.idStep || '',
			num: contractProp?.num || '',
			status: contractProp?.status || 'ACTIVE',
		};
	}, [contractProp, entity]);

	// state
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [contract, setContract] = useState<Contract | undefined>(contractProp);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [values, setValues] = useState(valuesDefault);

	// vars
	const idForm = 'form-manage-contract';
	const isEdit = Boolean(contract);
	const isAdd = !isEdit;

	// fetch entities
	const urlEntities = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=SUPPLIER`;

	useFetch({
		url: urlEntities,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch categories
	const urlCategories = `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`;

	useFetch({
		isEnabled: Boolean(project?.id),
		url: urlCategories,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCategories(res.data);
			}
		},
	});

	// fetch components
	const urlComponents = `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`;

	useFetch({
		isEnabled: Boolean(project?.id),
		url: urlComponents,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComponents(res.data);
			}
		},
	});

	// fetch expense types
	const urlExpenseTypes = `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`;

	useFetch({
		isEnabled: Boolean(project?.id),
		url: urlExpenseTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseTypes(res.data);
			}
		},
	});

	// fetch contract if paramDialogContract is present and contract state is not set
	const urlContract = paramDialogContract
		? `${process.env.REACT_APP_API_URL}/contracts/${paramDialogContract}?fields=amounts,categories,components,expenseTypes,id,idStep,num,status,description,dateSigning,entities&populate=categories,components,entities,expenseTypes`
		: undefined;

	useFetch({
		isEnabled: Boolean(urlContract) && !contract,
		url: urlContract,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data.length) {
				const contractNew = res.data[0];
				setContract(contractNew);
				setValues({
					amounts: contractNew.amounts || valuesDefault.amounts,
					categories: getIds((contractNew.categories as Category[] | number[] | undefined) || []),
					comment: valuesDefault.comment,
					components: getIds((contractNew.components as Component[] | number[] | undefined) || []),
					dateSigning: formatDate({ date: contractNew.dateSigning, format: 'YYYY-MM-DD' }),
					description: contractNew.description || valuesDefault.description,
					entities:
						(contractNew.entities as Entity[] | undefined)?.map((entityItem) => entityItem.id) ||
						valuesDefault.entities,
					expenseTypes: getIds(
						(contractNew.expenseTypes as ExpenseType[] | number[] | undefined) || []
					),
					idStep: contractNew.idStep || valuesDefault.idStep,
					num: contractNew.num || valuesDefault.num,
					status: contractNew.status || valuesDefault.status,
				});
			}
		},
	});

	// options
	const optionsCategories = categories.map((category) => ({
		label: category.name,
		value: category.id,
	}));

	const optionsComponents = components.map((component) => ({
		label: component.name,
		value: component.id,
	}));

	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: entity.id,
		};
	});

	const optionsExpenseTypes = expenseTypes.map((expenseType) => ({
		label: expenseType.name,
		value: expenseType.id,
	}));

	// handlers
	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleAmountsChange = (amounts: ContractAmount[] | ContractPayload['amounts']) => {
		setValues({
			...values,
			amounts: amounts as ContractAmount[],
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const entitiesNew = [...values.entities];

		if (!contract && userEntity?.id) {
			entitiesNew.push(userEntity.id);
		}

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// clear error
			setFeedback(null);

			// check for duplicate currencies
			const currenciesSelected = values.amounts?.map((amount) => amount.currency) || [];
			const currenciesUnique = new Set(currenciesSelected);
			if (currenciesSelected.length !== currenciesUnique.size) {
				feedbackNew.items.push({
					alert: {
						children: t('error.duplicateCurrency'),
						severity: 'error',
					},
				});
				setFeedback(feedbackNew);
				return;
			}

			// set loader
			setIsLoading(true);

			let commentId = null;
			let changedAmounts: ContractPayload['amounts'] = [];

			// If editing contract, check for amount changes
			if (isEdit) {
				// Compare current amounts with original amounts to find changes
				const originalAmounts = contract?.amounts || [];
				const currentCurrencies = values.amounts.map((amount) => amount.currency);
				const originalCurrencies = originalAmounts.map((amount) => amount.currency);

				// Find amounts that have changed (different values) or are new
				const modifiedAmounts = values.amounts.filter((currentAmount) => {
					const originalAmount = originalAmounts.find(
						(orig) => orig.currency === currentAmount.currency
					);

					// Include if it's a new currency or the amount has changed
					return !originalAmount || originalAmount.amount !== currentAmount.amount;
				});

				// Find currencies that were removed (set them to zero)
				const removedCurrencies = originalCurrencies.filter(
					(currency) => !currentCurrencies.includes(currency)
				);

				// Only include amounts that have changed or been removed
				const hasChanges = modifiedAmounts.length > 0 || removedCurrencies.length > 0;

				if (hasChanges) {
					changedAmounts = [
						// Include only modified/new amounts
						...modifiedAmounts.map((amount) => ({
							amount: amount.amount,
							currency: amount.currency,
						})),
						// Include removed currencies with zero amounts
						...removedCurrencies.map((currency) => ({
							amount: 0,
							currency: currency,
						})),
					];
				}

				if (values.comment) {
					const commentBody = {
						comment: values.comment,
						project: project?.id,
					};

					const fetchComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
						method: 'POST',
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
							'Content-Type': 'application/json',
						},
						body: JSON.stringify(commentBody),
					});
					const resComment = await fetchComment.json();

					if (resComment.error) {
						throw new Error(resComment.error.message, {
							cause: {
								id: resComment.error.id,
								res: resComment,
							},
						});
					}

					commentId = resComment.data.id;
				}
			} else {
				// For new contracts, include all amounts
				changedAmounts = values.amounts.map((amount) => ({
					amount: amount.amount,
					currency: amount.currency,
				}));
			}

			const body: ContractPayload = {
				categories: values.categories || [],
				components: values.components || [],
				dateSigning: formatDate({ date: values.dateSigning, format: 'YYYY-MM-DD' }) || undefined,
				description: values.description,
				entities: entitiesNew,
				expenseTypes: values.expenseTypes || [],
				idStep: values.idStep,
				num: values.num,
				status: values.status,
			};

			// Only include project ID if creating new contract
			if (!isEdit) {
				body.project = project?.id;
			}

			// Only include amounts if there are changes (for edits) or if creating new contract
			if (changedAmounts.length > 0) {
				body.amounts = changedAmounts;
			}

			// Add comment reference for amendments
			if (commentId) {
				body.comment = commentId;
			}

			let url = `${process.env.REACT_APP_API_URL}/contracts`;
			if (contract) {
				url += `/${contract.id}`;
			}

			const fetchAddContract = await fetch(url, {
				method: isEdit ? 'PATCH' : 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resAddContract = await fetchAddContract.json();

			if (resAddContract.error) {
				throw new Error(resAddContract.error.message, {
					cause: {
						details: { ...resAddContract.error?.details, id: contract?.id, num: entries.num },
						id: resAddContract.error.id,
						res: resAddContract,
					},
				});
			}

			const contractNew = resAddContract.data;

			// add documents using files
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/contracts/${contractNew.id}/${year}/${month}/`
				);
				formData.append('file', files[i]);
				formData.append('contract', String(contractNew.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				let fetchPostDoc: Response;

				try {
					fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
						method: 'post',
						body: formData,
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});
				} catch {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({ alert: { severity: 'error' }, response: resPostDoc });
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			// banner message
			let bannerMessage = t('alert.contractCreated', { num: entries.num });
			if (isEdit) {
				bannerMessage = t('alert.contractUpdated', { num: entries.num });
			}

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				// set success banner
				setBanner({
					hasClose: true,
					message: bannerMessage,
					severity: 'success',
				});

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackItem: Feedback = {
				alert: { children: translateError({ cause, t }), severity: 'error' },
			};

			if (cause?.res) {
				feedbackItem.response = cause.res;
			}

			feedbackNew.items.push(feedbackItem);
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id={`${idForm}`} name="formManageContract" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-num`}
						label={t('referenceNumber')}
						name="num"
						onChange={handleChangeValue}
						required={true}
						value={values.num}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						disabled={entities.length === 0}
						id={`${idForm}-entities`}
						label={t('suppliers')}
						multiple={true}
						name="entities"
						onChange={handleChangeValue}
						options={optionsEntities}
						required={true}
						value={values.entities}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-categories`}
						label={t('categories')}
						multiple={true}
						name="categories"
						onChange={handleChangeValue}
						options={optionsCategories}
						value={values.categories}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-components`}
						label={t('components')}
						multiple={true}
						name="components"
						onChange={handleChangeValue}
						options={optionsComponents}
						value={values.components}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-expense-types`}
						label={t('expenseTypes')}
						multiple={true}
						name="expenseTypes"
						onChange={handleChangeValue}
						options={optionsExpenseTypes}
						value={values.expenseTypes}
					/>
				</Grid>
				{isAdd && featureFlags['CONTRACT_WORKFLOW'] && (
					<Grid item xs={12}>
						<Select
							id={`${idForm}-status`}
							label={t('status')}
							name="status"
							onChange={handleChangeValue}
							options={[
								{ label: t('active'), value: 'ACTIVE' },
								{ label: t('draft'), value: 'DRAFT' },
								{ label: t('submitted'), value: 'SUBMITTED' },
							]}
							required={true}
							value={values.status}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<AmountsMultiCurrency
						amounts={values.amounts}
						onChange={handleAmountsChange}
						idForm={idForm}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-step-id`}
						label={t('stepId')}
						name="idStep"
						onChange={handleChangeValue}
						required={false}
						value={values.idStep}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-date-signing`}
						inputProps={{
							max: new Date().toISOString().split('T')[0],
						}}
						label={t('signingDate')}
						name="dateSigning"
						onChange={handleChangeValue}
						required={false}
						type="date"
						value={values.dateSigning}
					/>
				</Grid>
				{!contract && (
					<Grid item xs={12}>
						<FilePicker
							onChange={(e, filesSelected) => {
								setFiles(filesSelected);
							}}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Input
						id={`${idForm}-description`}
						label={t('description')}
						minRows={2}
						multiline={true}
						name="description"
						onChange={handleChangeValue}
						value={values.description}
					/>
				</Grid>
				{isEdit && (
					<Grid item xs={12}>
						<Input
							id={`${idForm}-comment`}
							label={t('comment')}
							name="comment"
							onChange={handleChangeValue}
							required={true}
							multiline={true}
							minRows={2}
							value={values.comment}
						/>
					</Grid>
				)}

				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
